/**
 * Employer Benefits Analytics Dashboard
 *
 * Phase 1 & 2 Implementation:
 * - Comprehensive ML-powered benefits analytics
 * - Employee retention risk analysis
 * - Cost predictions and forecasting
 * - Geographic risk mapping
 * - Benefits utilization metrics
 * - SDOH factor analysis
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Users,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Download,
  RefreshCw,
  Loader2,
  Activity,
  Heart,
  Target,
  Zap,
  Shield,
  MapPin,
  Rocket,
  BarChart3,
  Bell,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import { EmployerGeographicRiskMap } from './EmployerGeographicRiskMap';
import { EmployeeActionPlanDetail } from './EmployeeActionPlanDetail';
// Integrated employer dashboard components
import EmployerOnboardingWizard from './EmployerOnboardingWizard';
import EmployerQuickWins from './EmployerQuickWins';
import EmployerBenchmarking from './EmployerBenchmarking';
import EmployerCostTransparency from './EmployerCostTransparency';
import EmployerAlertsCenter from './EmployerAlertsCenter';
import { useTabbedChrome } from '@/components/weavein';

// Helper functions for null-safety
const safeToFixed = (value: number | undefined | null, decimals: number = 0): string => {
  if (value === undefined || value === null || isNaN(value)) return '0';
  return value.toFixed(decimals);
};

const safePercent = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return '0%';
  return `${(value * 100).toFixed(1)}%`;
};

const formatCurrency = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

interface EmployerAnalytics {
  employer_id: number;
  summary: {
    total_employees: number;
    active_employees?: number;
    avg_tenure_months: number;
    new_hires_count: number;
  };
  risk_distribution: {
    critical: number;
    high: number;
    elevated?: number;
    moderate: number;
    low: number;
  };
  engagement_metrics: {
    avg_churn_probability: number;
    avg_sdoh_score?: number;
    avg_engagement_score?: number;
    low_engagement_count: number;
  };
  sdoh_metrics?: {
    avg_housing_risk?: number;
    avg_transportation_risk?: number;
    avg_childcare_risk?: number;
    avg_financial_risk?: number;
    avg_medical_risk?: number;
    avg_climate_risk?: number;
    avg_housing_burden?: number;
    avg_unemployment_rate?: number;
    avg_poverty_rate?: number;
    avg_commute_time?: number;
    avg_rent_burden?: number;
    avg_ed_visits?: number;
  };
  sdoh_factor_distribution?: {
    housing_risk_count: number;
    transportation_risk_count: number;
    childcare_risk_count: number;
    financial_risk_count: number;
    medical_risk_count: number;
    multi_factor_risk_count: number;
    high_multi_factor_count: number;
  };
  department_breakdown?: Array<{
    department: string;
    employee_count: number;
    avg_churn_probability: number;
    avg_engagement?: number;
  }>;
  financial_impact: {
    estimated_annual_churn_cost: number;
    at_risk_employee_count: number;
    potential_savings: number;
    intervention_opportunities: number;
  };
}

interface CostPrediction {
  employer_id: number;
  plan_year: number;
  current_year_summary: {
    total_employees: number;
    avg_cost_per_employee: number;
    median_cost_per_employee: number;
    total_annual_cost: number;
    avg_claims_per_employee: number;
    avg_chronic_conditions: number;
  };
  cost_distribution: {
    very_high: number;
    high: number;
    moderate: number;
    low: number;
    very_low: number;
    high_cost_claimants: number;
  };
  projections: {
    next_year_projected_cost: number;
    trend_factor: number;
    cost_change: number;
  };
  department_breakdown: Array<{
    department: string;
    employee_count: number;
    at_risk_count: number;
    avg_sdoh_score: number;
    avg_churn_probability: number;
    estimated_annual_cost: number;
    cost_per_employee: number;
  }>;
}

interface BenefitsUtilization {
  employer_id: number;
  data_status: {
    total_employees: number;
    with_sdoh_data: number;
    data_coverage_pct: number;
  };
  enrollment_rates: {
    program_enrollment_rate?: number;
    avg_programs_per_employee?: number;
    active_programs?: number;
    dependent_coverage_rate?: number;
    hsa_rate?: number;
    fsa_rate?: number;
    dental_rate?: number;
    vision_rate?: number;
    wellness_rate?: number;
    pcp_assignment_rate?: number;
  };
  healthcare_utilization: {
    avg_pcp_visits_12m?: number;
    avg_ed_visits_12m?: number;
    avg_specialist_visits_12m?: number;
    avg_rx_adherence_pct?: number;
  };
  population_health: {
    with_medical_access_risk?: number;
    high_risk_employees?: number;
    avg_sdoh_score?: number;
    avg_medical_risk_score?: number;
  };
  utilization_metrics?: {
    avg_hsa_balance?: number;
    avg_hsa_contribution_rate?: number;
    avg_fsa_utilization_rate?: number;
    avg_pcp_visits_12m?: number;
    avg_preventive_visits_12m?: number;
    avg_ed_visits_12m?: number;
    avg_rx_adherence?: number;
  };
  engagement_metrics?: {
    avg_portal_logins_30d?: number;
    avg_benefits_engagement_score?: number;
    avg_plan_efficiency_score?: number;
  };
  totals: {
    total_employees: number;
    enrolled_in_programs?: number;
    total_program_enrollments?: number;
    covered_dependents?: number;
    hsa_enrolled?: number;
    fsa_enrolled?: number;
    with_dental?: number;
    dental_enrolled?: number;
    with_vision?: number;
    vision_enrolled?: number;
    with_pcp?: number;
    wellness_enrolled?: number;
  };
}

interface ContributingFactor {
  active: boolean;
  score: number;
  rent_burden_pct?: number;
  commute_minutes?: number;
  estimated_medical_debt?: number;
  ed_visits_12m?: number;
}

interface ActionPlanItem {
  category: string;
  action: string;
  urgency: string;
  impact: string;
}

interface InterventionRecommendation {
  employee_id: number;
  name: string;
  department: string;
  job_title?: string;
  tenure_months: number;
  churn_probability: number;
  engagement_score: number;
  sdoh_score: number;
  risk_level: string;
  risk_factor_count: number;
  priority_score: number;
  estimated_roi: number;
  contributing_factors: Record<string, ContributingFactor | boolean>;
  action_plan: ActionPlanItem[];
  recommended_interventions: Array<{
    type: string;
    action: string;
    impact: string;
    urgency: string;
  }>;
}

interface EmployerBenefitsAnalyticsDashboardProps {
  employerId: number;
}

export const EmployerBenefitsAnalyticsDashboard: React.FC<EmployerBenefitsAnalyticsDashboardProps> = ({
  employerId,
}) => {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const { token } = useAuth();

  // Check for default tab from navigation (e.g., from Benefits Hub)
  const getInitialTab = () => {
    const savedTab = localStorage.getItem('employee-retention_defaultTab');
    if (savedTab) {
      localStorage.removeItem('employee-retention_defaultTab'); // Clear after reading
      return savedTab;
    }
    return 'overview';
  };

  const [tabValue, setTabValue] = useState(getInitialTab);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Listen for subTab events from other components
  useEffect(() => {
    const handleSetSubTab = (event: CustomEvent) => {
      if (event.detail?.component === 'employee-retention' && event.detail?.tab) {
        setTabValue(event.detail.tab);
      }
    };

    window.addEventListener('setSubTab', handleSetSubTab as EventListener);
    return () => {
      window.removeEventListener('setSubTab', handleSetSubTab as EventListener);
    };
  }, []);

  // Data states
  const [analytics, setAnalytics] = useState<EmployerAnalytics | null>(null);
  const [costPredictions, setCostPredictions] = useState<CostPrediction | null>(null);
  const [benefitsUtilization, setBenefitsUtilization] = useState<BenefitsUtilization | null>(null);
  const [interventions, setInterventions] = useState<InterventionRecommendation[]>([]);

  // Filters - default to 'all' to show all employees initially
  const [interventionRiskFilter, setInterventionRiskFilter] = useState<string>('all');
  const [showAllEmployees, setShowAllEmployees] = useState<boolean>(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [selectedEmployeeForDetail, setSelectedEmployeeForDetail] = useState<InterventionRecommendation | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build intervention query params
      // When filter is 'all' or showAllEmployees is true, we need include_all=true
      const shouldIncludeAll = showAllEmployees || interventionRiskFilter === 'all';
      const interventionParams = new URLSearchParams({
        limit: '100',
        include_all: shouldIncludeAll ? 'true' : 'false',
      });
      if (!shouldIncludeAll && interventionRiskFilter !== 'all') {
        interventionParams.set('risk_level', interventionRiskFilter);
      }

      // Fetch all data in parallel
      const [analyticsRes, costRes, utilizationRes, interventionsRes] = await Promise.all([
        authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/analytics/summary`),
        authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/cost-predictions`),
        authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/benefits-utilization`),
        authFetch(`${getWeaveinApiUrl()}/api/employer/${employerId}/intervention-recommendations?${interventionParams}`),
      ]);

      if (!analyticsRes.ok) throw new Error('Failed to fetch analytics');

      const analyticsData = await analyticsRes.json();
      setAnalytics(analyticsData);

      if (costRes.ok) {
        const costData = await costRes.json();
        setCostPredictions(costData);
      }

      if (utilizationRes.ok) {
        const utilizationData = await utilizationRes.json();
        setBenefitsUtilization(utilizationData);
      }

      if (interventionsRes.ok) {
        const interventionsData = await interventionsRes.json();
        setInterventions(interventionsData.recommendations || []);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
      console.error('Error fetching employer analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [employerId, token, interventionRiskFilter, showAllEmployees]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getRiskBadgeColor = (level: string) => {
    switch (level) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'elevated': return 'default';
      case 'moderate': return 'secondary';
      case 'low': return 'outline';
      default: return 'secondary';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'immediate': return 'bg-red-500';
      case 'soon': return 'bg-orange-500';
      default: return 'bg-blue-500';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading analytics...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <p className="text-destructive">{error}</p>
            <Button onClick={fetchData} className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6 p-3 sm:p-6 max-w-full overflow-hidden">
      {/* Header - aligned with Member Enrollment page styling */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {!tabbed && (
          <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Users className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-purple-600 flex-shrink-0" />
            <span className="leading-tight">Benefits Analytics Dashboard</span>
          </h1>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            ML-powered insights for employee retention and benefits optimization
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics Overview */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                <span className="text-sm text-muted-foreground">Total Employees</span>
              </div>
              <p className="text-2xl font-bold mt-2">{analytics.summary.total_employees}</p>
              <p className="text-xs text-muted-foreground">
                {analytics.summary.new_hires_count} new hires
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-500" />
                <span className="text-sm text-muted-foreground">At-Risk Employees</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-orange-600">
                {analytics.risk_distribution.critical + analytics.risk_distribution.high}
              </p>
              <p className="text-xs text-muted-foreground">
                {safePercent(analytics.engagement_metrics.avg_churn_probability)} avg churn risk
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-500" />
                <span className="text-sm text-muted-foreground">Est. Churn Cost</span>
              </div>
              <p className="text-2xl font-bold mt-2">
                {formatCurrency(analytics.financial_impact?.estimated_annual_churn_cost)}
              </p>
              <p className="text-xs text-muted-foreground">
                {analytics.financial_impact?.at_risk_employee_count || 0} at-risk employees
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-purple-500" />
                <span className="text-sm text-muted-foreground">Potential Savings</span>
              </div>
              <p className="text-2xl font-bold mt-2 text-green-600">
                {formatCurrency(analytics.financial_impact.potential_savings)}
              </p>
              <p className="text-xs text-muted-foreground">
                from {analytics.financial_impact.intervention_opportunities} interventions
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={tabValue} onValueChange={setTabValue} className="w-full max-w-full overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300">
          <TabsList className="w-max min-w-full justify-start flex-nowrap">
            <TabsTrigger value="overview" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <Activity className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Overview</span>
              <span className="sm:hidden">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="risk-analysis" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <AlertTriangle className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Risk Analysis</span>
              <span className="sm:hidden">Risk</span>
            </TabsTrigger>
            <TabsTrigger value="cost-analytics" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Cost Analytics</span>
              <span className="sm:hidden">Cost</span>
            </TabsTrigger>
            <TabsTrigger value="benefits" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <Heart className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Benefits</span>
              <span className="sm:hidden">Benefits</span>
            </TabsTrigger>
            <TabsTrigger value="interventions" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <Target className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Interventions</span>
              <span className="sm:hidden">Actions</span>
            </TabsTrigger>
            <TabsTrigger value="geographic" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Risk Map</span>
              <span className="sm:hidden">Map</span>
            </TabsTrigger>
            <TabsTrigger value="getting-started" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <Rocket className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Getting Started</span>
              <span className="sm:hidden">Start</span>
            </TabsTrigger>
            <TabsTrigger value="quick-wins" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <Zap className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Quick Wins</span>
              <span className="sm:hidden">Wins</span>
            </TabsTrigger>
            <TabsTrigger value="benchmarking" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Benchmarking</span>
              <span className="sm:hidden">Bench</span>
            </TabsTrigger>
            <TabsTrigger value="cost-transparency" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <DollarSign className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Cost Transparency</span>
              <span className="sm:hidden">Costs</span>
            </TabsTrigger>
            <TabsTrigger value="alerts" className="flex items-center gap-1 sm:gap-2 whitespace-nowrap text-xs sm:text-sm px-2 sm:px-4">
              <Bell className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Alerts</span>
              <span className="sm:hidden">Alerts</span>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {analytics && (
            <>
              {/* Risk Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Risk Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-3xl font-bold text-red-600">{analytics.risk_distribution.critical}</p>
                      <p className="text-sm text-red-700">Critical</p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <p className="text-3xl font-bold text-orange-600">{analytics.risk_distribution.high}</p>
                      <p className="text-sm text-orange-700">High</p>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <p className="text-3xl font-bold text-yellow-600">{analytics.risk_distribution.moderate}</p>
                      <p className="text-sm text-yellow-700">Moderate</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-3xl font-bold text-green-600">{analytics.risk_distribution.low}</p>
                      <p className="text-sm text-green-700">Low</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SDOH Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">SDOH Risk Factors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Housing Risk</p>
                      <p className="text-xl font-semibold">{safeToFixed(analytics.sdoh_metrics?.avg_housing_risk, 1)}</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Transportation Risk</p>
                      <p className="text-xl font-semibold">{safeToFixed(analytics.sdoh_metrics?.avg_transportation_risk, 1)}</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Financial Risk</p>
                      <p className="text-xl font-semibold">{safeToFixed(analytics.sdoh_metrics?.avg_financial_risk, 1)}</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Rent Burden</p>
                      <p className="text-xl font-semibold">{safeToFixed(analytics.sdoh_metrics?.avg_rent_burden, 1)}%</p>
                    </div>
                    <div className="p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Avg Commute</p>
                      <p className="text-xl font-semibold">{safeToFixed(analytics.sdoh_metrics?.avg_commute_time, 0)} min</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* SDOH Factor Distribution */}
              {analytics.sdoh_factor_distribution && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">SDOH Factor Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-muted rounded-lg text-center">
                        <p className="text-2xl font-bold">{analytics.sdoh_factor_distribution.housing_risk_count}</p>
                        <p className="text-sm text-muted-foreground">Housing Risk</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg text-center">
                        <p className="text-2xl font-bold">{analytics.sdoh_factor_distribution.transportation_risk_count}</p>
                        <p className="text-sm text-muted-foreground">Transportation Risk</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg text-center">
                        <p className="text-2xl font-bold">{analytics.sdoh_factor_distribution.childcare_risk_count}</p>
                        <p className="text-sm text-muted-foreground">Childcare Risk</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg text-center">
                        <p className="text-2xl font-bold text-orange-600">{analytics.sdoh_factor_distribution.multi_factor_risk_count}</p>
                        <p className="text-sm text-muted-foreground">Multi-Factor Risk</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Risk Analysis Tab */}
        <TabsContent value="risk-analysis" className="space-y-4">
          {analytics && (
            <>
              {/* Financial Impact */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Financial Impact Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-red-700">Estimated Churn Cost</p>
                      <p className="text-2xl font-bold text-red-700">
                        {formatCurrency(analytics.financial_impact.estimated_annual_churn_cost)}
                      </p>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <p className="text-sm text-orange-700">At-Risk Employees</p>
                      <p className="text-2xl font-bold text-orange-700">
                        {analytics.financial_impact.at_risk_employee_count}
                      </p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-700">Potential Savings</p>
                      <p className="text-2xl font-bold text-green-700">
                        {formatCurrency(analytics.financial_impact.potential_savings)}
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-700">ROI Potential</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {analytics.financial_impact.estimated_annual_churn_cost > 0
                          ? safeToFixed(
                              (analytics.financial_impact.potential_savings /
                                analytics.financial_impact.estimated_annual_churn_cost) *
                                100,
                              0
                            )
                          : 0}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Engagement Metrics */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Engagement Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Avg Engagement Score</p>
                      <p className="text-3xl font-bold">{safeToFixed(analytics.engagement_metrics.avg_engagement_score, 0)}</p>
                      <p className="text-xs text-muted-foreground">out of 100</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Avg Churn Probability</p>
                      <p className="text-3xl font-bold">{safePercent(analytics.engagement_metrics.avg_churn_probability)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Low Engagement Count</p>
                      <p className="text-3xl font-bold text-orange-600">{analytics.engagement_metrics.low_engagement_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Cost Analytics Tab */}
        <TabsContent value="cost-analytics" className="space-y-4">
          {costPredictions && (
            <>
              {/* Cost Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Cost Summary - {costPredictions.plan_year}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Total Annual Cost</p>
                      <p className="text-2xl font-bold">{formatCurrency(costPredictions.current_year_summary.total_annual_cost)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Avg Cost per Employee</p>
                      <p className="text-2xl font-bold">{formatCurrency(costPredictions.current_year_summary.avg_cost_per_employee)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Median Cost</p>
                      <p className="text-2xl font-bold">{formatCurrency(costPredictions.current_year_summary.median_cost_per_employee)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Avg Claims/Employee</p>
                      <p className="text-2xl font-bold">{safeToFixed(costPredictions.current_year_summary.avg_claims_per_employee, 1)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Avg Chronic Conditions</p>
                      <p className="text-2xl font-bold">{safeToFixed(costPredictions.current_year_summary.avg_chronic_conditions, 1)}</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm text-red-700">High Cost Claimants</p>
                      <p className="text-2xl font-bold text-red-700">{costPredictions.cost_distribution.high_cost_claimants}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cost Projections */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Cost Projections</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-700">Projected Next Year</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {formatCurrency(costPredictions.projections.next_year_projected_cost)}
                      </p>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <p className="text-sm text-orange-700">Trend Factor</p>
                      <p className="text-2xl font-bold text-orange-700">
                        +{safeToFixed((costPredictions.projections.trend_factor - 1) * 100, 1)}%
                      </p>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-sm text-yellow-700">Expected Increase</p>
                      <p className="text-2xl font-bold text-yellow-700">
                        {formatCurrency(costPredictions.projections.cost_change)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Cost Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Cost Tier Distribution</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{costPredictions.cost_distribution.very_low}</p>
                      <p className="text-sm text-green-700">Very Low</p>
                      <p className="text-xs text-muted-foreground">&lt;$2K</p>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">{costPredictions.cost_distribution.low}</p>
                      <p className="text-sm text-blue-700">Low</p>
                      <p className="text-xs text-muted-foreground">$2K-$8K</p>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <p className="text-2xl font-bold text-yellow-600">{costPredictions.cost_distribution.moderate}</p>
                      <p className="text-sm text-yellow-700">Moderate</p>
                      <p className="text-xs text-muted-foreground">$8K-$20K</p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-600">{costPredictions.cost_distribution.high}</p>
                      <p className="text-sm text-orange-700">High</p>
                      <p className="text-xs text-muted-foreground">$20K-$50K</p>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <p className="text-2xl font-bold text-red-600">{costPredictions.cost_distribution.very_high}</p>
                      <p className="text-sm text-red-700">Very High</p>
                      <p className="text-xs text-muted-foreground">&gt;$50K</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Department Costs */}
              <Card className="overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-lg">Department Cost Breakdown</CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                  <div className="overflow-x-auto max-w-full">
                  <Table className="min-w-[600px]">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Department</TableHead>
                        <TableHead className="text-right">Employees</TableHead>
                        <TableHead className="text-right">SDOH Score</TableHead>
                        <TableHead className="text-right">Cost/Employee</TableHead>
                        <TableHead className="text-right">At Risk</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(costPredictions.department_breakdown || []).map((dept) => (
                        <TableRow key={dept.department}>
                          <TableCell className="font-medium">{dept.department || 'Unknown'}</TableCell>
                          <TableCell className="text-right">{dept.employee_count}</TableCell>
                          <TableCell className="text-right">{safeToFixed(dept.avg_sdoh_score, 1)}</TableCell>
                          <TableCell className="text-right">{formatCurrency(dept.cost_per_employee)}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant={dept.at_risk_count > 0 ? 'destructive' : 'secondary'}>
                              {dept.at_risk_count}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Benefits Tab */}
        <TabsContent value="benefits" className="space-y-4">
          {benefitsUtilization ? (
            <>
              {/* Data Coverage Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Data Coverage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <Users className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                      <p className="text-2xl font-bold">{benefitsUtilization.data_status?.total_employees || 0}</p>
                      <p className="text-sm text-muted-foreground">Total Employees</p>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <Activity className="h-8 w-8 mx-auto text-green-500 mb-2" />
                      <p className="text-2xl font-bold">{benefitsUtilization.data_status?.with_sdoh_data || 0}</p>
                      <p className="text-sm text-muted-foreground">With SDOH Data</p>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <TrendingUp className="h-8 w-8 mx-auto text-purple-500 mb-2" />
                      <p className="text-2xl font-bold">{safeToFixed(benefitsUtilization.data_status?.data_coverage_pct, 0)}%</p>
                      <p className="text-sm text-muted-foreground">Data Coverage</p>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <Target className="h-8 w-8 mx-auto text-orange-500 mb-2" />
                      <p className="text-2xl font-bold">{benefitsUtilization.enrollment_rates?.active_programs || 0}</p>
                      <p className="text-sm text-muted-foreground">Active Programs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Program Enrollment */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Program Enrollment</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Enrollment Rate</p>
                      <p className="text-2xl font-bold">{safeToFixed(benefitsUtilization.enrollment_rates?.program_enrollment_rate, 0)}%</p>
                      <p className="text-xs text-muted-foreground">{benefitsUtilization.totals?.enrolled_in_programs || 0} enrolled</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Avg Programs/Employee</p>
                      <p className="text-2xl font-bold">{safeToFixed(benefitsUtilization.enrollment_rates?.avg_programs_per_employee, 1)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Total Enrollments</p>
                      <p className="text-2xl font-bold">{benefitsUtilization.totals?.total_program_enrollments || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Healthcare Utilization */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Healthcare Utilization</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Avg PCP Visits (12m)</p>
                      <p className="text-2xl font-bold">{safeToFixed(benefitsUtilization.healthcare_utilization?.avg_pcp_visits_12m, 1)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Avg ED Visits (12m)</p>
                      <p className="text-2xl font-bold">{safeToFixed(benefitsUtilization.healthcare_utilization?.avg_ed_visits_12m, 1)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Specialist Visits (12m)</p>
                      <p className="text-2xl font-bold">{safeToFixed(benefitsUtilization.healthcare_utilization?.avg_specialist_visits_12m, 1)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">Rx Adherence</p>
                      <p className="text-2xl font-bold">{safeToFixed(benefitsUtilization.healthcare_utilization?.avg_rx_adherence_pct, 0)}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Population Health */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Population Health Risk</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">High Risk Employees</p>
                      <p className="text-2xl font-bold text-red-600">{benefitsUtilization.population_health?.high_risk_employees || 0}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Medical Access Risk</p>
                      <p className="text-2xl font-bold text-orange-600">{benefitsUtilization.population_health?.with_medical_access_risk || 0}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Avg SDOH Score</p>
                      <p className="text-2xl font-bold">{safeToFixed(benefitsUtilization.population_health?.avg_sdoh_score, 1)}</p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg text-center">
                      <p className="text-sm text-muted-foreground">Avg Medical Risk</p>
                      <p className="text-2xl font-bold">{safeToFixed(benefitsUtilization.population_health?.avg_medical_risk_score, 1)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Benefits utilization data is not available</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Interventions Tab */}
        <TabsContent value="interventions" className="space-y-4">
          {selectedEmployeeForDetail ? (
            /* Employee Detail View */
            <EmployeeActionPlanDetail
              employee={selectedEmployeeForDetail}
              onBack={() => setSelectedEmployeeForDetail(null)}
              onActionComplete={(stepId, employeeId) => {
                console.log(`Action ${stepId} completed for employee ${employeeId}`);
              }}
              onCreateTask={(taskData) => {
                console.log('Creating task:', taskData);
                // TODO: Integrate with task creation API
              }}
            />
          ) : (
            /* Employee List View */
            <>
              {/* Filters */}
              <Card>
                <CardContent className="pt-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Risk Level:</span>
                      <div className="flex gap-1">
                        {['all', 'critical', 'high', 'elevated', 'moderate', 'low'].map((level) => (
                          <Button
                            key={level}
                            variant={interventionRiskFilter === level ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                              setInterventionRiskFilter(level);
                              setShowAllEmployees(level === 'all');
                            }}
                            className="capitalize text-xs"
                          >
                            {level}
                          </Button>
                        ))}
                      </div>
                    </div>
                    <div className="ml-auto text-sm text-muted-foreground">
                      Showing {interventions.length} employees
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Employee List Table */}
              <Card className="overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-lg">Employee Action Plans</CardTitle>
                  <CardDescription>
                    Click on an employee to view detailed actions, risk factors, and intervention options
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                  {interventions.length > 0 ? (
                    <div className="overflow-x-auto max-w-full">
                      <Table className="min-w-[700px]">
                        <TableHeader>
                          <TableRow>
                            <TableHead>Employee</TableHead>
                            <TableHead>Risk Level</TableHead>
                            <TableHead className="text-center">SDOH Score</TableHead>
                            <TableHead className="text-center">Churn Risk</TableHead>
                            <TableHead className="text-center">Risk Factors</TableHead>
                            <TableHead className="text-right">Est. ROI</TableHead>
                            <TableHead className="text-center">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {interventions.map((rec) => (
                            <TableRow
                              key={rec.employee_id}
                              id={`employee-card-${rec.employee_id}`}
                              className={`cursor-pointer hover:bg-muted/50 transition-colors ${
                                selectedEmployeeId === rec.employee_id ? 'bg-blue-50' : ''
                              }`}
                              onClick={() => setSelectedEmployeeForDetail(rec)}
                            >
                              <TableCell>
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-2 h-8 rounded-full ${
                                      rec.risk_level === 'critical' ? 'bg-red-500' :
                                      rec.risk_level === 'high' ? 'bg-orange-500' :
                                      rec.risk_level === 'elevated' ? 'bg-amber-500' :
                                      rec.risk_level === 'moderate' ? 'bg-yellow-500' :
                                      'bg-green-500'
                                    }`}
                                  />
                                  <div>
                                    <div className="font-medium">{rec.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {rec.job_title || rec.department || 'Unknown'}
                                      {rec.department && rec.job_title && ` · ${rec.department}`}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge variant={getRiskBadgeColor(rec.risk_level) as 'default' | 'destructive' | 'outline' | 'secondary'}>
                                  {rec.risk_level}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="font-medium">{safeToFixed(rec.sdoh_score, 0)}</span>
                              </TableCell>
                              <TableCell className="text-center">
                                <span className={`font-medium ${
                                  rec.churn_probability > 0.7 ? 'text-red-600' :
                                  rec.churn_probability > 0.4 ? 'text-orange-600' :
                                  'text-green-600'
                                }`}>
                                  {safePercent(rec.churn_probability)}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center justify-center gap-1">
                                  {rec.risk_factor_count > 0 && (
                                    <AlertTriangle className={`h-4 w-4 ${
                                      rec.risk_factor_count >= 3 ? 'text-red-500' :
                                      rec.risk_factor_count >= 2 ? 'text-orange-500' :
                                      'text-yellow-500'
                                    }`} />
                                  )}
                                  <span>{rec.risk_factor_count}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="font-medium text-green-600">
                                  {formatCurrency(rec.estimated_roi)}
                                </span>
                              </TableCell>
                              <TableCell className="text-center">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedEmployeeForDetail(rec);
                                  }}
                                >
                                  View Plan
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 px-6 text-muted-foreground">
                      <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No employees found for the selected filter</p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          setInterventionRiskFilter('all');
                          setShowAllEmployees(true);
                        }}
                      >
                        Show All Employees
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Geographic Risk Map Tab */}
        <TabsContent value="geographic" className="space-y-4">
          <EmployerGeographicRiskMap
            employerId={employerId}
            onEmployeeClick={(empId) => {
              // Find the employee in interventions list and open detail view
              const employee = interventions.find(e => e.employee_id === empId);
              if (employee) {
                setSelectedEmployeeForDetail(employee);
                setTabValue('interventions');
              } else {
                // If not found in current list, navigate to interventions and highlight
                setSelectedEmployeeId(empId);
                setTabValue('interventions');
              }
            }}
          />
        </TabsContent>

        {/* Getting Started Tab */}
        <TabsContent value="getting-started" className="space-y-4">
          <EmployerOnboardingWizard
            employerId={employerId}
            onComplete={() => setTabValue('overview')}
            onSkip={() => setTabValue('overview')}
          />
        </TabsContent>

        {/* Quick Wins Tab */}
        <TabsContent value="quick-wins" className="space-y-4">
          <EmployerQuickWins employerId={employerId} />
        </TabsContent>

        {/* Benchmarking Tab */}
        <TabsContent value="benchmarking" className="space-y-4">
          <EmployerBenchmarking employerId={employerId} />
        </TabsContent>

        {/* Cost Transparency Tab */}
        <TabsContent value="cost-transparency" className="space-y-4">
          <EmployerCostTransparency employerId={employerId} />
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <EmployerAlertsCenter employerId={employerId} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmployerBenefitsAnalyticsDashboard;
